import React, { Suspense, useMemo, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Environment, Lightformer, Line, MeshTransmissionMaterial, RoundedBox } from "@react-three/drei";
import { AdditiveBlending, Euler, Vector3 } from "three";

const LUMINOUS_MEDIUM = "#f4f1e9";
const PEARL_LIGHT = "#fff2c8";
const COOL_LIGHT = "#c9e8ff";
const FRONT_DEPTH = 0.34;
const MOTION_SPEED = 0.68;

const VESSELS = [
  { size: 0.245, position: [-0.10, 0.03, 0.02], tint: "#fff8e8", role: "anchor" },
  { size: 0.150, position: [-0.39, 0.27, 0.12], tint: "#fffdf7" },
  { size: 0.136, position: [0.35, 0.30, -0.10], tint: "#faf8f1" },
  { size: 0.142, position: [0.38, -0.24, 0.13], tint: "#fffef9" },
  { size: 0.126, position: [-0.35, -0.30, -0.12], tint: "#f8f7f0" },
  { size: 0.090, position: [-0.08, 0.45, -0.19], tint: "#fffdf8" },
  { size: 0.080, position: [0.10, -0.43, 0.20], tint: "#fbfaf5" },
  { size: 0.072, position: [-0.47, 0.01, -0.20], tint: "#fffefb" },
  { size: 0.084, position: [0.48, 0.02, 0.18], tint: "#faf8f3" },
  { size: 0.064, position: [-0.27, 0.43, 0.25], tint: "#fffdf7" },
  { size: 0.070, position: [0.28, 0.43, 0.22], tint: "#faf8f1" },
  { size: 0.060, position: [-0.27, -0.43, 0.23], tint: "#fffef9" },
  { size: 0.068, position: [0.29, -0.40, -0.23], tint: "#f8f7f0" },
];

const ORBITS = [
  { radiusX: 0.66, radiusY: 0.34, radiusZ: 0.36, centerZ: 0.10, rotation: [0.52, 0.18, 0.18], phase: 0.02, cool: false },
  { radiusX: 0.70, radiusY: 0.30, radiusZ: 0.42, centerZ: 0.06, rotation: [-0.46, 0.52, 0.82], phase: 0.19, cool: true },
  { radiusX: 0.61, radiusY: 0.39, radiusZ: 0.31, centerZ: 0.18, rotation: [0.78, -0.42, -0.56], phase: 0.37, cool: false },
  { radiusX: 0.73, radiusY: 0.27, radiusZ: 0.46, centerZ: -0.02, rotation: [-0.72, -0.22, 0.38], phase: 0.54, cool: false },
  { radiusX: 0.57, radiusY: 0.43, radiusZ: 0.34, centerZ: 0.20, rotation: [0.28, 0.78, -0.92], phase: 0.68, cool: true },
  { radiusX: 0.67, radiusY: 0.32, radiusZ: 0.40, centerZ: 0.12, rotation: [0.94, 0.36, 1.06], phase: 0.81, cool: false },
];

const ACTIVITY_COUNT = 12;
const VESSEL_VISIT_ORDER = [0, 5, 2, 10, 8, 3, 6, 12, 4, 11, 7, 9, 1];

function buildOrbit(spec, segments = 80) {
  const center = new Vector3(VESSELS[0].position[0], VESSELS[0].position[1], spec.centerZ);
  const rotation = new Euler(...spec.rotation, "XYZ");
  const points = [];
  for (let i = 0; i <= segments; i += 1) {
    const angle = (i / segments) * Math.PI * 2;
    const point = new Vector3(
      Math.cos(angle) * spec.radiusX,
      Math.sin(angle) * spec.radiusY,
      Math.sin(angle + spec.phase * Math.PI * 2) * spec.radiusZ
    );
    point.applyEuler(rotation).add(center);
    points.push(point);
  }
  return points;
}

function sampleOrbit(points, progress, target) {
  const count = points.length - 1;
  const scaled = (((progress % 1) + 1) % 1) * count;
  const index = Math.floor(scaled);
  target.lerpVectors(points[index], points[(index + 1) % count], scaled - index);
}

function easeVisit(local) {
  const wave = Math.sin(Math.PI * local);
  return wave * wave * wave * wave;
}

function migrateThroughVessels(position, elapsed, activityIndex, targetPosition) {
  const cadence = 7.6 + (activityIndex % 4) * 0.9;
  const cycle = elapsed / cadence + activityIndex * 0.417;
  const step = Math.floor(cycle);
  const local = cycle - step;
  const stride = 5;
  const orderIndex = (step * stride + activityIndex * 3) % VESSEL_VISIT_ORDER.length;
  const vesselIndex = VESSEL_VISIT_ORDER[(orderIndex + VESSEL_VISIT_ORDER.length) % VESSEL_VISIT_ORDER.length];
  targetPosition.set(...VESSELS[vesselIndex].position);
  position.lerp(targetPosition, easeVisit(local));
}

function splitOrbitDepth(points) {
  const near = [], middle = [], far = [];
  const pushPoint = (bucket, point) => {
    const current = bucket[bucket.length - 1];
    if (!current || current._closed) bucket.push([point]); else current.push(point);
  };
  let previousZone = null;
  points.forEach((point) => {
    const zone = point.z >= FRONT_DEPTH ? "near" : point.z <= -0.18 ? "far" : "middle";
    const bucket = zone === "near" ? near : zone === "far" ? far : middle;
    if (zone !== previousZone && bucket.length) bucket[bucket.length - 1]._closed = true;
    pushPoint(bucket, point);
    previousZone = zone;
  });
  return { near, middle, far };
}

function FixedVessel({ spec, teamColor, isTeam = false, activityPositions }) {
  const innerGlow = useRef(), exitGlow = useRef();
  const center = useMemo(() => new Vector3(...spec.position), [spec.position]);
  const localLight = useMemo(() => new Vector3(), []), exitLight = useMemo(() => new Vector3(), []);

  useFrame(() => {
    if (!innerGlow.current || !exitGlow.current) return;
    let nearest = null, nearestDistance = Infinity;
    activityPositions.current.forEach((candidate) => {
      if (!candidate) return;
      const distance = center.distanceTo(candidate);
      if (distance < nearestDistance) { nearestDistance = distance; nearest = candidate; }
    });
    const response = nearest ? Math.max(0, 1 - nearestDistance / (spec.size + 0.105)) : 0;
    const transmissionResponse = response * response;
    if (nearest && response > 0) {
      localLight.copy(nearest).sub(center);
      if (localLight.lengthSq() > 0.00001) localLight.setLength(Math.min(localLight.length(), spec.size * 0.38));
      exitLight.copy(localLight).multiplyScalar(-0.62);
      innerGlow.current.position.copy(localLight);
      exitGlow.current.position.copy(exitLight);
    }
    innerGlow.current.material.opacity = transmissionResponse * (spec.role === "anchor" ? 0.46 : 0.38);
    innerGlow.current.scale.setScalar(0.72 + transmissionResponse * 0.52);
    exitGlow.current.material.opacity = transmissionResponse * (spec.role === "anchor" ? 0.24 : 0.20);
    exitGlow.current.scale.setScalar(0.66 + transmissionResponse * 0.42);
  });

  const vesselColor = isTeam ? teamColor : spec.tint;
  return <group position={spec.position}>
    <mesh ref={innerGlow} renderOrder={1}><sphereGeometry args={[spec.size * 0.52, 10, 10]} /><meshBasicMaterial color={PEARL_LIGHT} transparent opacity={0} depthWrite={false} depthTest blending={AdditiveBlending} /></mesh>
    <mesh ref={exitGlow} renderOrder={1}><sphereGeometry args={[spec.size * 0.34, 8, 8]} /><meshBasicMaterial color={isTeam ? teamColor : PEARL_LIGHT} transparent opacity={0} depthWrite={false} depthTest blending={AdditiveBlending} /></mesh>
    <mesh renderOrder={2}><sphereGeometry args={[spec.size, 28, 28]} /><meshPhysicalMaterial color={vesselColor} transmission={0.90} transparent opacity={0.82} thickness={spec.role === "anchor" ? 0.34 : 0.18} roughness={isTeam ? 0.105 : 0.065} ior={1.22} attenuationDistance={7.0} attenuationColor={vesselColor} clearcoat={0.48} clearcoatRoughness={isTeam ? 0.14 : 0.095} depthWrite={false} /></mesh>
  </group>;
}

function AtomicTraces({ orbits }) {
  return <group>{orbits.map((orbit, index) => {
    const color = orbit.cool ? COOL_LIGHT : PEARL_LIGHT;
    return <group key={index}>
      {orbit.depth.far.map((segment, i) => segment.length > 1 && <Line key={`far-${i}`} points={segment} color={color} lineWidth={0.24} transparent opacity={orbit.cool ? 0.070 : 0.058} depthWrite={false} depthTest renderOrder={0} />)}
      {orbit.depth.middle.map((segment, i) => segment.length > 1 && <Line key={`mid-${i}`} points={segment} color={color} lineWidth={0.30} transparent opacity={orbit.cool ? 0.13 : 0.105} depthWrite={false} depthTest renderOrder={1} />)}
      {orbit.depth.near.map((segment, i) => segment.length > 1 && <Line key={`near-${i}`} points={segment} color={color} lineWidth={0.38} transparent opacity={orbit.cool ? 0.22 : 0.18} depthWrite={false} depthTest={false} renderOrder={4} />)}
    </group>;
  })}</group>;
}

function OrbitalLight({ orbit, teamColor, engaged, phase = 0, speed = 1, strength = 1, teamTint = false, activityPositions, activityIndex }) {
  const travelling = useRef(), visibleCore = useRef(), visibleHalo = useRef();
  const position = useMemo(() => new Vector3(), []);
  const visitTarget = useMemo(() => new Vector3(), []);
  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();
    const duration = (engaged ? 2.55 : 3.15) / (speed * MOTION_SPEED);
    const progress = t / duration + phase + orbit.phase;
    sampleOrbit(orbit.points, progress, position);
    migrateThroughVessels(position, t, activityIndex, visitTarget);
    activityPositions.current[activityIndex].copy(position);
    const pulse = 0.72 + Math.sin(progress * Math.PI * 6) * 0.18;
    const isFront = position.z >= FRONT_DEPTH;
    if (travelling.current) { travelling.current.position.copy(position); travelling.current.intensity = (engaged ? 0.96 : 0.72) * strength * pulse; }
    if (visibleCore.current) { visibleCore.current.position.copy(position); visibleCore.current.scale.setScalar(0.78 + pulse * 0.42); visibleCore.current.material.opacity = 0.68 + pulse * 0.28; visibleCore.current.material.depthTest = !isFront; visibleCore.current.renderOrder = isFront ? 6 : 3; }
    if (visibleHalo.current) { visibleHalo.current.position.copy(position); visibleHalo.current.scale.setScalar((isFront ? 1.20 : 0.92) + pulse * 0.54); visibleHalo.current.material.opacity = (isFront ? 0.11 : 0.07) + pulse * 0.06; visibleHalo.current.material.depthTest = !isFront; visibleHalo.current.renderOrder = isFront ? 5 : 3; }
  });
  const color = orbit.cool ? COOL_LIGHT : (teamTint ? teamColor : PEARL_LIGHT);
  return <><pointLight ref={travelling} color={color} distance={1.0} decay={1.8} intensity={0} /><mesh ref={visibleHalo}><sphereGeometry args={[0.041, 10, 10]} /><meshBasicMaterial color={color} transparent opacity={0} depthWrite={false} depthTest /></mesh><mesh ref={visibleCore}><sphereGeometry args={[0.017, 10, 10]} /><meshBasicMaterial color={color} transparent opacity={0.85} depthWrite={false} depthTest /></mesh></>;
}

function OrbitalSpark({ orbit, engaged, phase = 0, speed = 2.4, teamColor, teamTint = false, size = 1, activityPositions, activityIndex }) {
  const core = useRef(), halo = useRef();
  const position = useMemo(() => new Vector3(), []);
  const visitTarget = useMemo(() => new Vector3(), []);
  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();
    const duration = (engaged ? 1.75 : 2.15) / (speed * MOTION_SPEED);
    const progress = t / duration + phase + orbit.phase;
    sampleOrbit(orbit.points, progress, position);
    migrateThroughVessels(position, t, activityIndex, visitTarget);
    activityPositions.current[activityIndex].copy(position);
    const pulse = 0.76 + Math.sin(progress * Math.PI * 8) * 0.16;
    const isFront = position.z >= FRONT_DEPTH;
    if (core.current) { core.current.position.copy(position); core.current.scale.setScalar((0.72 + pulse * 0.38) * size * (isFront ? 1.16 : 1)); core.current.material.opacity = (isFront ? 0.78 : 0.66) + pulse * 0.22; core.current.material.depthTest = !isFront; core.current.renderOrder = isFront ? 6 : 3; }
    if (halo.current) { halo.current.position.copy(position); halo.current.scale.setScalar((0.90 + pulse * 0.48) * size * (isFront ? 1.28 : 1)); halo.current.material.opacity = (isFront ? 0.085 : 0.055) + pulse * 0.045; halo.current.material.depthTest = !isFront; halo.current.renderOrder = isFront ? 5 : 3; }
  });
  const color = orbit.cool ? COOL_LIGHT : (teamTint ? teamColor : PEARL_LIGHT);
  return <><mesh ref={halo}><sphereGeometry args={[0.030 * size, 9, 9]} /><meshBasicMaterial color={color} transparent opacity={0} depthWrite={false} depthTest /></mesh><mesh ref={core}><sphereGeometry args={[0.010 * size, 9, 9]} /><meshBasicMaterial color={color} transparent opacity={0.8} depthWrite={false} depthTest /></mesh></>;
}

function RelationalLight({ teamColor, engaged, orbits, activityPositions }) {
  return <group>{Array.from({ length: ACTIVITY_COUNT }, (_, index) => {
    const orbit = orbits[index % orbits.length];
    return index < 6
      ? <OrbitalLight key={index} orbit={orbit} teamColor={teamColor} engaged={engaged} phase={index * 0.117} speed={0.82 + (index % 3) * 0.12} strength={0.74 + (index % 4) * 0.08} teamTint={index % 5 === 0} activityPositions={activityPositions} activityIndex={index} />
      : <OrbitalSpark key={index} orbit={orbit} teamColor={teamColor} engaged={engaged} phase={index * 0.093} speed={1.1 + (index % 4) * 0.16} teamTint={index % 4 === 0} size={0.8 + (index % 3) * 0.18} activityPositions={activityPositions} activityIndex={index} />;
  })}</group>;
}

function PresenceObject({ teamColor, engaged, shellVariant }) {
  const group = useRef();
  const activityPositions = useRef(Array.from({ length: ACTIVITY_COUNT }, () => new Vector3(9, 9, 9)));
  const orbits = useMemo(() => ORBITS.map((spec) => { const points = buildOrbit(spec); return { ...spec, points, depth: splitOrbitDepth(points) }; }), []);
  useFrame(({ clock }) => {
    if (!group.current) return;
    const t = clock.getElapsedTime();
    const settle = 1 + Math.sin(t * (Math.PI * 2) / 18) * 0.0008;
    group.current.scale.setScalar(settle * (engaged ? 1.004 : 1));
    group.current.rotation.x = -0.016 + Math.sin(t * 0.020) * 0.0018;
    group.current.rotation.y = Math.cos(t * 0.018) * 0.0025;
  });
  const smooth = shellVariant === "smooth";
  return <group ref={group}>
    <RoundedBox args={[1.54, 1.54, 1.54]} radius={0.31} smoothness={6}>
      <meshBasicMaterial color="#f6f2ea" transparent opacity={0.014} depthWrite={false} />
    </RoundedBox>
    <AtomicTraces orbits={orbits} />
    {VESSELS.map((spec, index) => <FixedVessel key={index} spec={spec} teamColor={teamColor} isTeam={index === 1} activityPositions={activityPositions} />)}
    <RelationalLight teamColor={teamColor} engaged={engaged} orbits={orbits} activityPositions={activityPositions} />
    <RoundedBox args={[1.76, 1.76, 1.76]} radius={0.36} smoothness={8} creaseAngle={0.44} renderOrder={8}>
      <MeshTransmissionMaterial
        transmission={1}
        thickness={smooth ? 0.72 : 0.52}
        roughness={smooth ? 0.016 : 0.04}
        ior={smooth ? 1.36 : 1.38}
        chromaticAberration={smooth ? 0.004 : 0.002}
        anisotropy={smooth ? 0.018 : 0.008}
        distortion={smooth ? (engaged ? 0.028 : 0.012) : (engaged ? 0.008 : 0.003)}
        distortionScale={smooth ? 0.08 : 0.028}
        temporalDistortion={smooth ? (engaged ? 0.008 : 0.002) : 0}
        attenuationDistance={smooth ? 10.5 : 16}
        attenuationColor="#fffdfa"
        clearcoat={smooth ? 1 : 0.76}
        clearcoatRoughness={smooth ? 0.018 : 0.07}
        background={LUMINOUS_MEDIUM}
        depthWrite={false}
      />
    </RoundedBox>
  </group>;
}

export default function TotoPresence({ teamColor = "#5c8670", engaged = false, className = "", shellVariant = "matte" }) {
  const smooth = shellVariant === "smooth";
  return <div className={`toto-presence-canvas ${className}`} aria-hidden="true">
    <Canvas dpr={[1, 1.35]} camera={{ position: [0, 0.06, 4.6], fov: 30 }} gl={{ antialias: true, alpha: true, powerPreference: "high-performance" }} style={{ background: "transparent" }}>
      <ambientLight intensity={smooth ? 1.02 : 1.08} color="#fffaf2" />
      <hemisphereLight skyColor="#ffffff" groundColor="#d4cec6" intensity={smooth ? 0.92 : 0.96} />
      <directionalLight position={[3.5, 4.4, 4]} intensity={smooth ? 1.08 : 0.86} color="#fffaf0" />
      <directionalLight position={[-3.2, 1.4, -2]} intensity={smooth ? 0.44 : 0.46} color="#eaf2f5" />
      <pointLight position={[0, -2.3, 3]} intensity={0.10} color={teamColor} />
      <pointLight position={[0, 0.2, -2]} intensity={smooth ? 0.30 : 0.48} color="#fff1cb" />
      <Suspense fallback={null}>
        <Environment resolution={64} frames={1} background={false}>
          <Lightformer form="rect" intensity={smooth ? 3.1 : 2.35} position={[0, 4, 3]} rotation={[-Math.PI / 3, 0, 0]} scale={[7, 2.5, 1]} color="#fffdf8" />
          <Lightformer form="rect" intensity={smooth ? 1.42 : 1.10} position={[-4, 0.5, 2]} rotation={[0, Math.PI / 2, 0]} scale={[4, 3, 1]} color="#fff4e3" />
          <Lightformer form="rect" intensity={smooth ? 1.12 : 0.92} position={[4, 0.4, 1]} rotation={[0, -Math.PI / 2, 0]} scale={[4, 3, 1]} color="#edf7fa" />
          <Lightformer form="ring" intensity={smooth ? 0.48 : 0.52} position={[0, -3, 1]} rotation={[Math.PI / 2, 0, 0]} scale={[4, 4, 1]} color="#fff8ea" />
        </Environment>
        <PresenceObject teamColor={teamColor} engaged={engaged} shellVariant={shellVariant} />
      </Suspense>
    </Canvas>
  </div>;
}
